/** Plate verdict: pure types shared by client and server. No I/O lives in this folder. */

/** Unit components a verdict can be about (v1 scope). */
export const VERDICT_COMPONENTS = ['generator', 'engine', 'alternator', 'ats', 'ats2'] as const;
export type VerdictComponent = (typeof VERDICT_COMPONENTS)[number];

export const VERDICT_FIELDS = ['make', 'model', 'serial'] as const;
export type VerdictField = (typeof VERDICT_FIELDS)[number];

/** What the Gemini reader said the plate belongs to (readDataPlate PLATE_COMPONENTS + cannot-tell). */
export type ReaderComponent = 'generator-set' | 'engine' | 'alternator' | 'ats' | 'other' | 'not-a-plate' | 'cannot-tell';

export type ReaderFieldStatus = 'read' | 'partial' | 'unreadable' | 'not-on-plate';

/** One photo's readings, already adapted from a plateReads doc. */
export interface PlateReading {
  photoId: string;
  storagePath: string;
  /** ISO time the photo was taken or uploaded; used to order visits. */
  capturedAt: string | null;
  readerComponent: ReaderComponent;
  componentConfidence: number;
  /** The photo row's own label (person / capture / serial-match), when present. It outranks the reader's guess. */
  labeledComponent: VerdictComponent | null;
  fields: Record<VerdictField, { status: ReaderFieldStatus; value: string | null; confidence: number }>;
  /** Full Vision OCR text of the same image, or null when Vision did not run. */
  ocrText: string | null;
  /** Text decoded from a barcode on the plate (exact), or null when none was found (Task 2.3). */
  barcodeText: string | null;
}

/** The version stamped on every stored verdict and feedback doc; the 90% tally counts only feedback at this version. ONE constant. */
export const PLATE_VERDICT_VERSION = 'v1' as const;

export type VerdictKind =
  | 'match'          // plate agrees with the stored value
  | 'fill'           // stored value is blank; plate gives a value (objectives 1 and 3)
  | 'conflict'       // stored value differs from the plate (objective 2)
  | 'likely-typo'    // a HUMAN-entered value is 1-2 characters off the plate (objective 4)
  | 'format-variant' // differs only by leading zeros
  | 'unsure'         // AI cannot decide; reason says why
  | 'no-plate';      // no usable plate photo for this component

/**
 * The verdict kinds that ASK a person to do something: the ones the 90% hide rule counts and can hide. match, unsure and no-plate ask
 * nothing and are never hidden. ONE list for the server's tally and the screens' `isAskingKind`, so they cannot drift apart.
 */
export const ASKING_VERDICT_KINDS = ['fill', 'conflict', 'likely-typo', 'format-variant'] as const satisfies readonly VerdictKind[];

export type VerdictReason =
  | 'readers-agree'
  | 'barcode-confirmed'
  | 'readers-disagree'
  | 'wrong-component'
  | 'partial-read'
  | 'unreadable'
  | 'lookalike'
  /** The plate value is part of the saved value ("3512" vs "CAT 3512B"): using it would cut the saved value short (never one-click). */
  | 'contained'
  | 'anchor-mismatch'
  /** make/model only: the plate's own serial could not be read and confirmed, so the photo cannot be tied to this unit. */
  | 'anchor-unchecked'
  | 'photos-disagree'
  /** The value being judged is present but unreadable by us (e.g. only non-Latin characters, or absurdly long): never treated as blank. */
  | 'unusable-record'
  | 'no-reading';

export interface Verdict {
  component: VerdictComponent;
  field: VerdictField;
  kind: VerdictKind;
  /** high = both readers agree, component matches, identity anchor holds. Only high verdicts may be offered as one-click. */
  confidence: 'high' | 'low';
  reason: VerdictReason;
  /** The value as printed on the plate (best reading), sanitized for display (no control/bidi characters, max 64 chars + ellipsis), or null. */
  plateValue: string | null;
  /** The value we compared against (stored or human-entered), sanitized for display like plateValue. */
  comparedValue: string | null;
  /** The photo the plateValue came from, for the panel to show. */
  photoId: string | null;
  storagePath: string | null;
  /** Indexes into the NORMALIZED plateValue (A-Z0-9 only, no label/spacing; not the raw or displayed text) whose character differs from the normalized comparedValue. Empty when either value is over the comparison cap. */
  diffPositions: number[];
  /** How many matching-component photos read this field (status 'read'), and how many of those were independently confirmed and agreed with plateValue. */
  photosRead: number;
  photosAgreeing: number;
}

/** A verdict as stored in organizations/{org}/plateVerdicts and served to the client (Chunk 3). Shared so client and server agree. */
export interface StoredVerdict extends Verdict {
  organizationId: string;
  generatorDocId: string;
  recordValueAtCompute: string;
  /** True when the verdict's photo carries a label for THIS component (person, capture or unit pointer), not only the reader's guess. */
  photoLabeled: boolean;
  computedAt: string;
  verdictVersion: typeof PLATE_VERDICT_VERSION;
}

/** The 90% hide rule's state for the org (Task 6.3). */
export interface PlateVerdictTally {
  state: 'too-few' | 'shown' | 'hidden';
  decisions: number;
  usePlate: number;
}

/** fieldService_plateVerdictsRead response (Task 3.5). */
export interface PlateVerdictsReadResponse {
  enabled: boolean;
  generatorDocId: string | null;
  verdicts: Array<StoredVerdict & { photoUrl: string | null }>;
  readings: PlateReading[];
  storedSerials: Partial<Record<VerdictComponent, string>>;
  unitHasAts2: boolean;
  /** true = every asking verdict (fill, conflict, likely-typo, format-variant), stored OR judged in the browser, renders hidden. */
  hideAsking: boolean;
  tally: PlateVerdictTally | null;
}
